/* eslint-disable react-hooks/exhaustive-deps */
import { AllegatoTipoDocumentoEnum, ClienteDto, IndirizziAzienda, SedeDto } from "@api/client";
import { companyUpdateCompany } from "common/_redux/company/actions";
import { companyDetailsSelector } from "common/_redux/company/selectors";
import { fileUpload } from "common/_redux/file/actions";
import BftBooleanButton from "common/components/boolean-button";
import BftButton from "common/components/button";
import CountriesDropdown from "common/components/countries-dropdown";
import BftInfoTooltip from "common/components/info-tooltip";
import BftInput from "common/components/input";
import BftSelect from "common/components/select";
import { TipoSedeEnum } from "common/enums/sede.enum";
import { FilesUtils } from "common/file.utils";
import { indirizziAziendaSchema } from "common/validators/validators";
import { COUNTRIES, COUNTRIES_PREFIX, COUNTRY_ITA } from "constants/countries";
import { BooleanValues } from "constants/global";
import { commonGetComuni } from "core/store/actions";
import { useAppDispatch, useAppSelector } from "core/store/hooks";
import { commonComuniSelector, commonProvinceSelector } from "core/store/selectors";
import { FieldArray, Formik } from "formik";
import { FileDocument } from "models/files";
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { DropdownProps } from "semantic-ui-react";
import IndirizziContattiAltraSede from "./indirizzi-contatti-altra-sede";
import IndirizziContattiSedeItem from "./indirizzi-contatti-sede-item";

const { TRUE, FALSE } = BooleanValues;

interface IndirizziAziendaExt extends IndirizziAzienda {
	corrispondenzaCartacea: boolean;
}

export interface SedeDtoExt extends SedeDto {
	prepostoIsRL: BooleanValues;
	SCO_CONTRATTO_UTENZA_LOCAZIONEDCM?: FileDocument | undefined;
}

export interface FormValues {
	indirizziAzienda: IndirizziAziendaExt;
	elencoSedi: SedeDtoExt[];
}

interface Props {
	onComplete: () => void;
}

const IndirizziEContatti: React.FC<Props> = (props) => {
	const { onComplete } = props;

	const dispatch = useAppDispatch();

	const companyDetails = useAppSelector(companyDetailsSelector);
	const province = useAppSelector(commonProvinceSelector);
	const comuni = useAppSelector(commonComuniSelector);

	const [showFormCorrispondenzaCartacea, setShowFormCorrispondenzaCartacea] =
		useState<boolean>(false);

	const handleProvinciaChange = (provincia: string | null | undefined) => {
		if (provincia) dispatch(commonGetComuni(provincia));
	};

	const handleSubmit = (values: FormValues) => {
		let documents: FileDocument[] = [];

		let altraSedeOperativa = values?.elencoSedi?.find(
			(s) => s?.tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA && s?.operativa
		);

		if (
			altraSedeOperativa &&
			altraSedeOperativa?.SCO_CONTRATTO_UTENZA_LOCAZIONEDCM &&
			altraSedeOperativa?.SCO_CONTRATTO_UTENZA_LOCAZIONEDCM?.size > 0
		) {
			documents = [altraSedeOperativa?.SCO_CONTRATTO_UTENZA_LOCAZIONEDCM];
		}

		const data: ClienteDto = {
			...companyDetails,
			...values,
			elencoSedi: [
				...values?.elencoSedi
					?.filter(
						(s) =>
							s?.tipoSede != TipoSedeEnum.ALTRA_SEDE_OPERATIVA ||
							(s?.tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA && s?.operativa)
					)
					?.map((s) => {
						let res = s;

						if (s?.prepostoIsRL == TRUE) {
							res = {
								...s,
								nomeSoggettoPreposto: undefined,
								cognomeSoggettoPreposto: undefined,
								emailSoggettoPreposto: undefined,
								telefonoSoggettoPreposto: undefined,
							};
						}

						return res;
					}),
			],
		};

		dispatch(fileUpload(documents as FileDocument[]))
			.unwrap()
			.then((response) => {
				if (response || documents.length < 1) {
					dispatch(companyUpdateCompany(data))
						.unwrap()
						.then((res) => {
							if (res) onComplete();
						});
				}
			});
	};

	useEffect(() => {
		if (companyDetails) {
			handleProvinciaChange(companyDetails?.indirizziAzienda?.provinciaSedeLegale);

			if (
				companyDetails?.indirizziAzienda?.provinciaSedeLegale !=
				companyDetails?.indirizziAzienda?.provinciaCorrispondenza
			)
				handleProvinciaChange(companyDetails?.indirizziAzienda?.provinciaCorrispondenza);

			let altraSedeOperativa = companyDetails?.elencoSedi?.find(
				(s) => s?.tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA && s?.operativa
			);
			if (altraSedeOperativa) {
				handleProvinciaChange(altraSedeOperativa?.provincia);
			}
		}
	}, [companyDetails]);

	return (
		<Formik
			initialValues={
				{
					indirizziAzienda: {
						telefono: companyDetails?.indirizziAzienda?.telefono || "",
						prefissoTelefono: "+39",
						emailAziendale:
							companyDetails?.indirizziAzienda?.emailAziendale?.toLocaleLowerCase() || "",
						pec: companyDetails?.indirizziAzienda?.pec?.toLocaleLowerCase() || "",
						capSedeLegale: companyDetails?.indirizziAzienda?.capSedeLegale || "",
						nazioneSedeLegale: "IT",
						provinciaSedeLegale: companyDetails?.indirizziAzienda?.provinciaSedeLegale || "",
						comuneSedeLegale: companyDetails?.indirizziAzienda?.comuneSedeLegale || "",
						indirizzoSedeLegale: companyDetails?.indirizziAzienda?.indirizzoSedeLegale || "",
						civicoSedeLegale: companyDetails?.indirizziAzienda?.civicoSedeLegale || "",
						capCorrispondenza: companyDetails?.indirizziAzienda?.capCorrispondenza || undefined,
						nazioneCorrispondenza:
							companyDetails?.indirizziAzienda?.nazioneCorrispondenza || undefined,
						provinciaCorrispondenza:
							companyDetails?.indirizziAzienda?.provinciaCorrispondenza || undefined,
						comuneCorrispondenza:
							companyDetails?.indirizziAzienda?.comuneCorrispondenza || undefined,
						indirizzoCorrispondenza:
							companyDetails?.indirizziAzienda?.indirizzoCorrispondenza || undefined,
						civicoCorrispondenza:
							companyDetails?.indirizziAzienda?.civicoCorrispondenza || undefined,
						corrispondenzaCartacea: !!companyDetails?.indirizziAzienda?.capCorrispondenza,
					},
					elencoSedi: companyDetails?.elencoSedi
						? [
								...companyDetails?.elencoSedi?.map((s, i) => {
									if (s?.tipoSede != TipoSedeEnum.ALTRA_SEDE_OPERATIVA)
										return {
											...s,
											prepostoIsRL: !!s?.nomeSoggettoPreposto ? FALSE : TRUE,
										} as SedeDtoExt;

									return {
										...s,
										prepostoIsRL: !!s?.nomeSoggettoPreposto ? FALSE : TRUE,
										SCO_CONTRATTO_UTENZA_LOCAZIONEDCM: FilesUtils.AllegatoDtoToFileDocument(
											companyDetails?.elencoAllegati?.find(
												(f) =>
													f.idTipoDocumento == AllegatoTipoDocumentoEnum.ContrattoUtenzaLocazione
											)
										),
									} as SedeDtoExt;
								}),
						  ]
						: undefined,
				} as FormValues
			}
			onSubmit={handleSubmit}
			validationSchema={indirizziAziendaSchema}
			enableReinitialize
		>
			{({ handleSubmit, handleChange, setFieldValue, handleBlur, values, touched, errors }) => {
				const handleChangeCustom =
					(input: string, type?: string) => (e: any, d?: DropdownProps) => {
						handleChange(e);
						if (type === "nations") {
							setFieldValue(input, (d?.value as string)?.toUpperCase());
							if (!showFormCorrispondenzaCartacea && input === "nazioneSedeLegale") {
								setFieldValue(
									"indirizziAzienda.nazioneCorrispondenza",
									(d?.value as string)?.toUpperCase()
								);
							}
							return;
						}
						if (type === "province") {
							setFieldValue(input, e.target.value);
							handleProvinciaChange(e.target.value);
							if (
								!showFormCorrispondenzaCartacea &&
								input === "indirizziAzienda.provinciaSedeLegale"
							) {
								setFieldValue("indirizziAzienda.provinciaCorrispondenza", e.target.value);
							}
							return;
						}
						setFieldValue(input, e === TRUE);
					};

				const handleMatchSedeLegaleCorrispondenzaCartacea = (e: string | undefined) => {
					if (e === TRUE) {
						setShowFormCorrispondenzaCartacea(false);
						setFieldValue("indirizziAzienda.corrispondenzaCartacea", false);
						setFieldValue("indirizziAzienda.capCorrispondenza", undefined);
						setFieldValue("indirizziAzienda.nazioneCorrispondenza", undefined);
						setFieldValue("indirizziAzienda.provinciaCorrispondenza", undefined);
						setFieldValue("indirizziAzienda.comuneCorrispondenza", undefined);
						setFieldValue("indirizziAzienda.indirizzoCorrispondenza", undefined);
						setFieldValue("indirizziAzienda.civicoCorrispondenza", undefined);
					}
					if (e === FALSE) {
						setShowFormCorrispondenzaCartacea(true);
						setFieldValue("indirizziAzienda.corrispondenzaCartacea", true);
						setFieldValue("indirizziAzienda.capCorrispondenza", "");
						setFieldValue("indirizziAzienda.nazioneCorrispondenza", "it");
						setFieldValue("indirizziAzienda.provinciaCorrispondenza", "");
						setFieldValue("indirizziAzienda.comuneCorrispondenza", "");
						setFieldValue("indirizziAzienda.indirizzoCorrispondenza", "");
						setFieldValue("indirizziAzienda.civicoCorrispondenza", "");
					}
				};

				const handleChangeSedeLegale = (input: string) => (event: any) => {
					setFieldValue(input, event.target.value);
					// if (!showFormCorrispondenzaCartacea) {
					// 	switch (input) {
					// 		case "indirizziAzienda.capSedeLegale":
					// 			setFieldValue("indirizziAzienda.capCorrispondenza", event.target.value);
					// 			break;
					// 		case "indirizziAzienda.comuneSedeLegale":
					// 			setFieldValue("indirizziAzienda.comuneCorrispondenza", event.target.value);
					// 			break;
					// 		case "indirizziAzienda.indirizzoSedeLegale":
					// 			setFieldValue("indirizziAzienda.indirizzoCorrispondenza", event.target.value);
					// 			break;
					// 		case "indirizziAzienda.civicoSedeLegale":
					// 			setFieldValue("indirizziAzienda.civicoCorrispondenza", event.target.value);
					// 			break;
					// 	}
					// }
				};

				return (
					<form onSubmit={handleSubmit}>
						<>
							<h5 className="container">Contatti</h5>

							<Row className="justify-content-between">
								<Col md={3}>
									<CountriesDropdown
										options={COUNTRIES_PREFIX}
										containerClass="mb-3"
										value={values?.indirizziAzienda?.prefissoTelefono}
										onChange={handleChangeCustom("indirizziAzienda.prefissoTelefono", "nations")}
									/>
								</Col>

								<Col md={9}>
									<BftInput
										required
										type={"number"}
										name={"indirizziAzienda.telefono"}
										placeholder={"Telefono ufficio*"}
										value={values?.indirizziAzienda?.telefono}
										onChange={handleChange}
										isInvalid={
											!!errors?.indirizziAzienda?.telefono && touched?.indirizziAzienda?.telefono
										}
									/>
									{errors?.indirizziAzienda?.telefono && touched?.indirizziAzienda?.telefono && (
										<Form.Control.Feedback className="ms-3" type="invalid">
											{errors?.indirizziAzienda?.telefono}
										</Form.Control.Feedback>
									)}
								</Col>
							</Row>

							<BftInput
								required
								type={"email"}
								name={"indirizziAzienda.emailAziendale"}
								placeholder={"Email aziendale*"}
								value={values?.indirizziAzienda?.emailAziendale}
								onChange={handleChange}
								isInvalid={
									!!errors?.indirizziAzienda?.emailAziendale &&
									touched?.indirizziAzienda?.emailAziendale
								}
							/>
							{errors?.indirizziAzienda?.emailAziendale &&
								touched?.indirizziAzienda?.emailAziendale && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors?.indirizziAzienda?.emailAziendale}
									</Form.Control.Feedback>
								)}

							<Row>
								<BftInput
									required
									type={"email"}
									name={"indirizziAzienda.pec"}
									placeholder={"PEC*"}
									value={values?.indirizziAzienda?.pec}
									onChange={handleChange}
									isInvalid={!!errors?.indirizziAzienda?.pec && touched?.indirizziAzienda?.pec}
								/>
								{errors?.indirizziAzienda?.pec && touched?.indirizziAzienda?.pec && (
									<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
										{errors?.indirizziAzienda?.pec}
									</Form.Control.Feedback>
								)}
							</Row>

							<h5 className="container my-3">Sede legale</h5>

							<Row>
								<Col md={5}>
									<BftSelect
										name={"indirizziAzienda.provinciaSedeLegale"}
										placeholder={"Provincia*"}
										value={values?.indirizziAzienda?.provinciaSedeLegale}
										onChange={handleChangeCustom(
											"indirizziAzienda.provinciaSedeLegale",
											"province"
										)}
										isInvalid={
											!!errors?.indirizziAzienda?.provinciaSedeLegale &&
											touched?.indirizziAzienda?.provinciaSedeLegale
										}
										disabled={
											!["IT", "", undefined].includes(
												values?.indirizziAzienda?.nazioneSedeLegale?.toUpperCase()
											)
										}
										options={province}
									/>
									{errors?.indirizziAzienda?.provinciaSedeLegale &&
										touched?.indirizziAzienda?.provinciaSedeLegale && (
											<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
												{errors?.indirizziAzienda?.provinciaSedeLegale}
											</Form.Control.Feedback>
										)}
								</Col>
								<Col md={7}>
									{["IT", "", undefined].includes(
										values?.indirizziAzienda?.nazioneSedeLegale?.toUpperCase()
									) ? (
										<>
											<BftSelect
												name={"indirizziAzienda.comuneSedeLegale"}
												placeholder={"Comune*"}
												value={values?.indirizziAzienda?.comuneSedeLegale}
												onChange={handleChangeSedeLegale("indirizziAzienda.comuneSedeLegale")}
												isInvalid={
													!!errors?.indirizziAzienda?.comuneSedeLegale &&
													touched?.indirizziAzienda?.comuneSedeLegale
												}
												options={comuni?.[values?.indirizziAzienda?.provinciaSedeLegale!]}
												disabled={values?.indirizziAzienda?.provinciaSedeLegale === ""}
											/>
											{errors?.indirizziAzienda?.comuneSedeLegale &&
												touched?.indirizziAzienda?.comuneSedeLegale && (
													<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
														{errors?.indirizziAzienda?.comuneSedeLegale}
													</Form.Control.Feedback>
												)}
										</>
									) : (
										<>
											<BftInput
												required
												type={"text"}
												name={"indirizziAzienda.comuneSedeLegale"}
												placeholder={"Comune*"}
												value={values?.indirizziAzienda?.comuneSedeLegale}
												onChange={handleChangeSedeLegale("indirizziAzienda.comuneSedeLegale")}
												isInvalid={
													!!errors?.indirizziAzienda?.comuneSedeLegale &&
													touched?.indirizziAzienda?.comuneSedeLegale
												}
											/>
											{errors?.indirizziAzienda?.comuneSedeLegale &&
												touched?.indirizziAzienda?.comuneSedeLegale && (
													<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
														{errors?.indirizziAzienda?.comuneSedeLegale}
													</Form.Control.Feedback>
												)}
										</>
									)}
								</Col>
							</Row>
							<Row>
								<Col md={5}>
									<BftInput
										required
										type={"number"}
										name={"indirizziAzienda.capSedeLegale"}
										placeholder={"CAP*"}
										value={values?.indirizziAzienda?.capSedeLegale}
										onChange={handleChangeSedeLegale("indirizziAzienda.capSedeLegale")}
										isInvalid={
											!!errors?.indirizziAzienda?.capSedeLegale &&
											touched?.indirizziAzienda?.capSedeLegale
										}
									/>
									{errors?.indirizziAzienda?.capSedeLegale &&
										touched?.indirizziAzienda?.capSedeLegale && (
											<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
												{errors?.indirizziAzienda?.capSedeLegale}
											</Form.Control.Feedback>
										)}
								</Col>
								<Col md={7}>
									<div className="d-flex align-items-center">
										<CountriesDropdown
											options={COUNTRY_ITA}
											containerClass="mb-3"
											value={values?.indirizziAzienda?.nazioneSedeLegale}
											onChange={handleChangeCustom("indirizziAzienda.nazioneSedeLegale", "nations")}
										/>
										<BftInfoTooltip
											className="mb-3 mb-3"
											message="Gentile Cliente, al momento accettiamo solo società con sede legale in Italia."
										/>
									</div>
								</Col>
							</Row>
							<Row>
								<Col md={9}>
									<BftInput
										required
										type={"text"}
										name={"indirizziAzienda.indirizzoSedeLegale"}
										placeholder={"Indirizzo*"}
										value={values?.indirizziAzienda?.indirizzoSedeLegale?.toUpperCase()}
										onChange={(e) => {
											const { name, value } = e.target;
											setFieldValue(name, value.toUpperCase());
											// handleChangeSedeLegale("indirizziAzienda.indirizzoSedeLegale");
										}}
										isInvalid={
											!!errors?.indirizziAzienda?.indirizzoSedeLegale &&
											touched?.indirizziAzienda?.indirizzoSedeLegale
										}
									/>
									{errors?.indirizziAzienda?.indirizzoSedeLegale &&
										touched?.indirizziAzienda?.indirizzoSedeLegale && (
											<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
												{errors?.indirizziAzienda?.indirizzoSedeLegale}
											</Form.Control.Feedback>
										)}
								</Col>

								<Col md={3}>
									<BftInput
										required
										type={"text"}
										name={"indirizziAzienda.civicoSedeLegale"}
										placeholder={"N°*"}
										value={values?.indirizziAzienda?.civicoSedeLegale}
										onChange={handleChangeSedeLegale("indirizziAzienda.civicoSedeLegale")}
										isInvalid={
											!!errors?.indirizziAzienda?.civicoSedeLegale &&
											touched?.indirizziAzienda?.civicoSedeLegale
										}
									/>
									{errors?.indirizziAzienda?.civicoSedeLegale &&
										touched?.indirizziAzienda?.civicoSedeLegale && (
											<Form.Control.Feedback className="ms-3 mb-3" type="invalid">
												{errors?.indirizziAzienda?.civicoSedeLegale}
											</Form.Control.Feedback>
										)}
								</Col>
							</Row>
						</>

						<div className="d-flex align-items-end">
							<div>
								<h5 className="container my-3">
									Sedi operative nelle quali si intende svolgere il servizio oggetto del contratto
								</h5>
							</div>
							<BftInfoTooltip
								className="mb-3"
								message="Seleziona le sedi operative della società"
							/>
						</div>

						<div className="container">
							<FieldArray
								name="elencoSedi"
								render={(arrayHelpers) => (
									<>
										{values?.elencoSedi?.map((sede, index: number) => {
											if (sede?.tipoSede == TipoSedeEnum.ALTRA_SEDE_OPERATIVA) return null;

											if (
												sede?.cap === values.indirizziAzienda.capSedeLegale &&
												sede?.indirizzo === values.indirizziAzienda.indirizzoSedeLegale &&
												sede?.numeroCivico === values.indirizziAzienda.civicoSedeLegale
											) {
												return null;
											}

											return (
												<IndirizziContattiSedeItem
													key={`sede-operativa_${index}`}
													sedeIndex={index}
													values={values}
													errors={errors}
													touched={touched}
													handleChange={handleChange}
													handleBlur={handleBlur}
												/>
											);
										})}

										<IndirizziContattiAltraSede
											arrayHelpers={arrayHelpers}
											values={values}
											errors={errors}
											touched={touched}
											handleChange={handleChange}
											handleBlur={handleBlur}
											setFieldValue={setFieldValue}
										/>
									</>
								)}
							/>
						</div>

						<div className="d-flex align-items-end">
							<div>
								<h5 className="container my-3">Corrispondenza cartacea</h5>
							</div>
							<BftInfoTooltip
								className="mb-3"
								message="Indirizzo dove ricevere comunicazioni tramite posta"
							/>
						</div>

						<Row>
							<Col>
								<BftBooleanButton
									label="Coincide con la sede legale?"
									name="corrispondenzaCartacea"
									value={String(!showFormCorrispondenzaCartacea)}
									onChange={handleMatchSedeLegaleCorrispondenzaCartacea}
								/>
								{errors?.indirizziAzienda?.corrispondenzaCartacea &&
									touched?.indirizziAzienda?.corrispondenzaCartacea &&
									!showFormCorrispondenzaCartacea && (
										<Form.Control.Feedback className="ms-3" type="invalid">
											{errors?.indirizziAzienda?.corrispondenzaCartacea}
										</Form.Control.Feedback>
									)}
							</Col>
						</Row>

						{showFormCorrispondenzaCartacea && (
							<>
								<Row>
									<Col md={5}>
										<BftSelect
											name={"indirizziAzienda.provinciaCorrispondenza"}
											placeholder={"Provincia*"}
											value={values?.indirizziAzienda?.provinciaCorrispondenza}
											onChange={handleChangeCustom("provinciaCorrispondenza", "province")}
											isInvalid={
												!!errors?.indirizziAzienda?.provinciaCorrispondenza &&
												touched?.indirizziAzienda?.provinciaCorrispondenza
											}
											options={province}
										/>
										{errors?.indirizziAzienda?.provinciaCorrispondenza &&
											touched?.indirizziAzienda?.provinciaCorrispondenza && (
												<Form.Control.Feedback className="ms-3" type="invalid">
													{errors?.indirizziAzienda?.provinciaCorrispondenza}
												</Form.Control.Feedback>
											)}
									</Col>
									<Col md={7}>
										<BftSelect
											name={"indirizziAzienda.comuneCorrispondenza"}
											placeholder={"Comune*"}
											value={values?.indirizziAzienda?.comuneCorrispondenza}
											onChange={handleChange}
											isInvalid={
												!!errors?.indirizziAzienda?.comuneCorrispondenza &&
												touched?.indirizziAzienda?.comuneCorrispondenza
											}
											options={comuni?.[values?.indirizziAzienda?.provinciaCorrispondenza!]}
										/>
										{errors?.indirizziAzienda?.comuneCorrispondenza &&
											touched?.indirizziAzienda?.comuneCorrispondenza && (
												<Form.Control.Feedback className="ms-3" type="invalid">
													{errors?.indirizziAzienda?.comuneCorrispondenza}
												</Form.Control.Feedback>
											)}
									</Col>
								</Row>
								<Row>
									<Col md={5}>
										<BftInput
											required
											type={"number"}
											name={"indirizziAzienda.capCorrispondenza"}
											placeholder={"CAP*"}
											value={values?.indirizziAzienda?.capCorrispondenza}
											onChange={handleChange}
											isInvalid={
												!!errors?.indirizziAzienda?.capCorrispondenza &&
												touched?.indirizziAzienda?.capCorrispondenza
											}
										/>
										{errors?.indirizziAzienda?.capCorrispondenza &&
											touched?.indirizziAzienda?.capCorrispondenza && (
												<Form.Control.Feedback className="ms-3" type="invalid">
													{errors?.indirizziAzienda?.capCorrispondenza}
												</Form.Control.Feedback>
											)}
									</Col>
									<Col md={7}>
										<CountriesDropdown
											options={COUNTRIES}
											value={values?.indirizziAzienda?.nazioneCorrispondenza}
											onChange={handleChangeCustom(
												"indirizziAzienda.nazioneCorrispondenza",
												"nations"
											)}
										/>
									</Col>
								</Row>

								<Row>
									<Col md={9}>
										<BftInput
											required
											type={"text"}
											name={"indirizziAzienda.indirizzoCorrispondenza"}
											placeholder={"Indirizzo*"}
											value={values?.indirizziAzienda?.indirizzoCorrispondenza?.toUpperCase()}
											onChange={(e) => {
												const { name, value } = e.target;
												setFieldValue(name, value.toUpperCase());
											}}
											isInvalid={
												!!errors?.indirizziAzienda?.indirizzoCorrispondenza &&
												touched?.indirizziAzienda?.indirizzoCorrispondenza
											}
										/>
										{errors?.indirizziAzienda?.indirizzoCorrispondenza &&
											touched?.indirizziAzienda?.indirizzoCorrispondenza && (
												<Form.Control.Feedback className="ms-3" type="invalid">
													{errors?.indirizziAzienda?.indirizzoCorrispondenza}
												</Form.Control.Feedback>
											)}
									</Col>
									<Col md={3}>
										<BftInput
											required
											type={"text"}
											name={"indirizziAzienda.civicoCorrispondenza"}
											placeholder={"N°*"}
											value={values?.indirizziAzienda?.civicoCorrispondenza}
											onChange={handleChange}
											isInvalid={
												!!errors?.indirizziAzienda?.civicoCorrispondenza &&
												touched?.indirizziAzienda?.civicoCorrispondenza
											}
										/>
										{errors?.indirizziAzienda?.civicoCorrispondenza &&
											touched?.indirizziAzienda?.civicoCorrispondenza && (
												<Form.Control.Feedback className="ms-3" type="invalid">
													{errors?.indirizziAzienda?.civicoCorrispondenza}
												</Form.Control.Feedback>
											)}
									</Col>
								</Row>
							</>
						)}

						<div className="mx-2 my-4">
							<BftButton type="submit" className="btn-buffetti col-12">
								Avanti
							</BftButton>
						</div>
					</form>
				);
			}}
		</Formik>
	);
};
export default IndirizziEContatti;
